import React, { useState } from 'react';
import { TextField, DefaultButton, PrimaryButton } from '@fluentui/react';

interface EditPaneProps {
    selectedRole: any;
    editPaneMessage: string | null | undefined;
    submitDisabled: boolean;
    handlePaneClose: () => void;
    handleRoleDefinitionSubmit: (updatedRole: any) => void;
    capitalizeFirstLetter: (key: string) => string;
}

const getInitialCondition = (role: any): string => {
    if (role && Array.isArray(role.permissions) && role.permissions.length > 0) {
        return role.permissions[0].condition || '';
    }
    return '';
}

const getInitialPermissionActions = (role: any): string[] => {
    if (role && Array.isArray(role.permissions) && role.permissions.length > 0) {
        return role.permissions[0].actions || [];
    }
    return [];
};

const getInitialPermissionNotActions = (role: any): string[] => {
    if (role && Array.isArray(role.permissions) && role.permissions.length > 0) {
        return role.permissions[0].notActions || [];
    }
    return [];
};

const EditPane: React.FC<EditPaneProps> = ({
  selectedRole,
  editPaneMessage,
  submitDisabled,
  handlePaneClose,
  handleRoleDefinitionSubmit,
  capitalizeFirstLetter
}) => {
  const [editedDescription, setEditedDescription] = useState(() => selectedRole.description || selectedRole.roleDescription || '');
  const [editedName, setEditedName] = useState(() => selectedRole.name || selectedRole.roleName || '');
  const [editedGroupName, setEditedGroupName] = useState(() => selectedRole.businessGroupName || '');
  const [editedPolicyType, setEditedPolicyType] = useState(() => selectedRole.policyType || '');
  const [editedSkipLevel, setEditedSkipLevel] = useState(() => selectedRole.allowSkipLevel || '');
  const [editedEndDate, setEditedEndDate] = useState(() => selectedRole.endDate || '');
  const [editedScope, setEditedScope] = useState(() => Array.isArray(selectedRole.scopes) ? selectedRole.scopes.join(', ') : (selectedRole.scopes || selectedRole.scope || ''));
  const [editedAttribute, setEditedAttribute] = useState(() => JSON.stringify(selectedRole.attributes) || '');
  const [editedCondition, setEditedCondition] = useState(() => getInitialCondition(selectedRole));
  const [editedPermissionActions, setEditedPermissionActions] = useState(() => getInitialPermissionActions(selectedRole));
  const [editedPermissionNotActions, setEditedPermissionNotActions] = useState(() => getInitialPermissionNotActions(selectedRole));

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedDescription(e.target.value);
  };
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedName(e.target.value);
  };
  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedGroupName(e.target.value);
  };
  const handlePolicyTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedPolicyType(e.target.value);
  };
  const handleSkipLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedSkipLevel(e.target.value);
  };
  const handleScopeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedScope(e.target.value);
  };
  const handleAttributeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedAttribute(e.target.value);
  };
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedEndDate(e.target.value);
  };
  const handlePermissionActionChange = (idx: number, e: any) => {
    const value = e.target.value;
    setEditedPermissionActions(
      prev => {
        const actions = [...prev];
        actions[idx] = value;
        return actions;
      }
    )
  };
  const addPermissionAction = () => {
    setEditedPermissionActions(prev => [...prev, '']);
  };
  const removePermissionAction = (idx: number) => {
    setEditedPermissionActions(prev => prev.filter((_, i) => i !== idx));
  };
  const handlePermissionNotActionChange = (idx: number, newValue: string) => {
    const value = newValue;
    setEditedPermissionNotActions(
      prev => {
        const notActions = [...prev];
        notActions[idx] = value;
        return notActions;
      }
    )
  };
  const addPermissionNotAction = () => {
    setEditedPermissionNotActions(prev => [...prev, '']);
  };
  const removePermissionNotAction = (idx: number) => {
    setEditedPermissionNotActions(prev => prev.filter((_, i) => i !== idx));
  };
  const handlePermissionConditionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedCondition(e.target.value);
  };

  const handleSubmit = () => {
    if (!selectedRole) return;
    const updatedRole = {
      ...selectedRole,
      description: editedDescription,
      name: editedName,
      scopes: editedScope.split(',').map(s => s.trim()),
      businessGroupName: editedGroupName,
      policyType: editedPolicyType,
      roleName: editedName,
      allowSkipLevel: editedSkipLevel,
      endDate: editedEndDate,
      attributes: editedAttribute != '' ? JSON.parse(editedAttribute) : null,
      permissions: [
        {
          actions: editedPermissionActions.filter(x => x.trim()),
          notActions: editedPermissionNotActions.filter(x => x.trim()),
          condition: editedCondition
        }
      ],
    };
    handleRoleDefinitionSubmit(updatedRole);
  };

  if (!selectedRole) return null;

  const excludeKeys = Object.keys(selectedRole).filter(
    key => !['tenantname', 'tenantid', 'nextpageuri', 'nextpageurl', 'nextpageurl'.toLowerCase(), 'policy', 'isactive'].includes(key.toLowerCase())
  );

  return (
    <div className="edit-pane">
      <DefaultButton className="edit-pane-close" onClick={handlePaneClose}>Close</DefaultButton>
      <h3 className="edit-pane-title">Edit</h3>
      <div style={{ paddingBottom: 20 }}>
        {excludeKeys.map(key => (
          <div className="mr" key={key}>
            <strong>{capitalizeFirstLetter(key)}:</strong><br />
            {(key === 'description' || key === 'roleDescription') ? (
              <TextField
                type="text"
                value={editedDescription}
                onChange={handleDescriptionChange}
                className="edit-pane-input"
              />
            ) : (key === 'roleName' || key === 'name' || key === 'RoleName') ? (
              <TextField
                type="text"
                value={editedName}
                onChange={handleNameChange}
                className="edit-pane-input"
              />
            ) : (key === 'policyType') ? (
              <TextField
                type="text"
                value={editedPolicyType}
                onChange={handlePolicyTypeChange}
                className="edit-pane-input"
              />
            ) : (key === 'businessGroupName') ? (
              <TextField
                type="text"
                value={editedGroupName}
                onChange={handleGroupNameChange}
                className="edit-pane-input"
              />
            ) : (key === 'allowSkipLevel') ? (
              <TextField
                type="text"
                value={editedSkipLevel}
                onChange={handleSkipLevelChange}
                className="edit-pane-input"
              />
            ) : key === 'scopes' || key === 'scope' ? (
              <TextField
                type="text"
                value={editedScope}
                onChange={handleScopeChange}
                className="edit-pane-input"
              />
            ) : key === 'attributes' ? (
              <TextField
                type="text"
                value={editedAttribute}
                onChange={handleAttributeChange}
                className="edit-pane-input"
              />
            ) : key === 'endDate' ? (
              <TextField
                type="text"
                value={editedEndDate}
                onChange={handleEndDateChange}
                className="edit-pane-input"
              />
            ) : key === 'permissions' ? (
              <div className="edit-pane-permission-box">
                <label className="edit-pane-label">Actions:</label>
                {editedPermissionActions.map((action, idx) => (
                  <div key={idx} className="edit-pane-permission-row">
                    <TextField
                      type="text"
                      value={action}
                      onChange={(e,value) => handlePermissionActionChange(idx, e)}
                      className="edit-pane-input"
                      placeholder="Action"
                    />
                    <DefaultButton type="DefaultButton" className="edit-pane-permission-btn" onClick={() => removePermissionAction(idx)}>-</DefaultButton>
                  </div>
                ))}
                <DefaultButton className="edit-pane-add-btn" onClick={addPermissionAction}>Add Action</DefaultButton>
                <br />
                <label className="edit-pane-label">Not Actions:</label>
                {editedPermissionNotActions.map((notAction, idx) => (
                  <div key={idx} className="edit-pane-permission-row">
                    <TextField
                      type="text"
                      value={notAction}
                      onChange={(e, newValue) => handlePermissionNotActionChange(idx, newValue)}
                      className="edit-pane-input"
                      placeholder="NotAction"
                    />
                    <DefaultButton className="edit-pane-permission-btn" onClick={() => removePermissionNotAction(idx)}>-</DefaultButton>
                  </div>
                ))}
                <DefaultButton className="edit-pane-add-btn" onClick={addPermissionNotAction}>Add NotAction</DefaultButton>
                <br />
                <label className="edit-pane-label">Condition:</label>
                <TextField
                  type="text"
                  value={editedCondition}
                  onChange={handlePermissionConditionChange}
                  className="edit-pane-input"
                  placeholder="Condition expression"
                />
              </div>
            ) : (
              <span>{typeof selectedRole[key] === 'object' && selectedRole[key] !== null ? JSON.stringify(selectedRole[key]) : String(selectedRole[key] ?? '')}</span>
            )}
          </div>
        ))}
        <PrimaryButton className="edit-pane-submit-btn" onClick={handleSubmit} disabled={submitDisabled}>
          Submit
        </PrimaryButton>
        {editPaneMessage && (
          <div className={`edit-pane-message ${editPaneMessage.toLowerCase().includes('success') ? 'success' : 'error'}`}>
            {editPaneMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditPane;
